import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useRef, type JSX } from "react";
import type { MessageId, MessageSummary } from "@mailflare/shared";

import { MessageRow } from "./message-row";

export interface MessageListProps {
  readonly messages: readonly MessageSummary[];
  readonly selectedId: MessageId | null;
  readonly focusedIndex: number;
  readonly ariaLabel: string;
  readonly hasNextPage: boolean;
  readonly isFetchingNextPage: boolean;
  readonly onSelect: (message: MessageSummary) => void;
  readonly onToggleFlag: (message: MessageSummary) => void;
  readonly onLoadMore: () => void;
}

const ROW_HEIGHT = 84;

/**
 * Virtualized message list. Only the visible window is rendered, so a mailbox
 * with 100k messages still mounts a constant number of DOM nodes.
 */
export function MessageList({
  messages,
  selectedId,
  focusedIndex,
  ariaLabel,
  hasNextPage,
  isFetchingNextPage,
  onSelect,
  onToggleFlag,
  onLoadMore,
}: MessageListProps): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const lastItem = virtualItems[virtualItems.length - 1];

  useEffect(() => {
    if (
      lastItem !== undefined &&
      lastItem.index >= messages.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      onLoadMore();
    }
  }, [lastItem, messages.length, hasNextPage, isFetchingNextPage, onLoadMore]);

  useEffect(() => {
    if (focusedIndex >= 0 && focusedIndex < messages.length) {
      virtualizer.scrollToIndex(focusedIndex, { align: "auto" });
    }
  }, [focusedIndex, messages.length, virtualizer]);

  return (
    <div
      ref={scrollRef}
      className="min-h-0 flex-1 overflow-y-auto"
      data-testid="message-list-scroll"
    >
      <div
        role="listbox"
        aria-label={ariaLabel}
        style={{ height: virtualizer.getTotalSize() }}
        className="relative"
      >
        {virtualItems.map((virtualItem) => {
          const message = messages[virtualItem.index];
          if (message === undefined) return null;
          return (
            <div
              key={message.id}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: virtualItem.size,
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <MessageRow
                message={message}
                selected={message.id === selectedId}
                focused={virtualItem.index === focusedIndex}
                onSelect={onSelect}
                onToggleFlag={onToggleFlag}
              />
            </div>
          );
        })}
      </div>
      {isFetchingNextPage ? (
        <p className="text-muted-foreground p-3 text-center text-xs">Loading more…</p>
      ) : null}
    </div>
  );
}
